import { ApolloProvider } from '@apollo/client';
import {
  apolloClient,
  COMMON_IGNORED_SENTRY_ERRORS,
  filterSentryEvent,
  RootLoader,
} from '@retainerclub/common';
import '@retainerclub/common/src/config/i18n/i18next';
import * as Sentry from '@sentry/react';
import { Amplify } from 'aws-amplify';
import React, { Suspense } from 'react';
import { CookiesProvider } from 'react-cookie';
import ReactDOM from 'react-dom/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';
import App from './App';
import awsAuthConfig from './config/amplify/awsAuthConfig';

import './index.css';

const levels =
  import.meta.env.VITE_APP_ENV !== 'production' ? ['error', 'warn'] : ['error'];

Sentry.init({
  dsn: 'https://1f83a3758f713b915bf079b5bec66b10@o1097881.ingest.us.sentry.io/4507137807876096',
  enabled: import.meta.env.VITE_APP_ENV !== 'development',
  environment: import.meta.env.VITE_APP_ENV,
  release: `rc-partner-locator@${import.meta.env.VITE_APP_VERSION}`,
  ignoreErrors: [
    ...COMMON_IGNORED_SENTRY_ERRORS,
  ],
  beforeSend: filterSentryEvent,
  integrations: [
    Sentry.captureConsoleIntegration({ levels }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      blockAllImages: false,
      blockAllAudio: true,
      blockAllVideo: true,
      blockAllIframes: true,
      blockAllScripts: true,
      blockAllForms: false,
      blockAllPopups: true,
    }),
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.browserProfilingIntegration(),
    Sentry.browserSessionIntegration(),
    Sentry.graphqlClientIntegration({
      endpoints: [
        'localhost',
        'https://api.retainerclub.com/rcgc',
        'https://api-qa.retainerclub.com/rcgc',
      ],
    }),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: ['localhost', /^https:\/\/retainerclub.com/],
  replaysSessionSampleRate: 0.15,
  replaysOnErrorSampleRate: 1.0,
});

const ampConfig = awsAuthConfig[import.meta.env.VITE_APP_ENV];

Amplify.configure({ ...ampConfig });

ReactDOM.createRoot(document.getElementById('root'), {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <React.StrictMode>
    <Suspense fallback={<RootLoader />}>
      <ApolloProvider client={apolloClient}>
        <CookiesProvider>
          <App />
        </CookiesProvider>
      </ApolloProvider>
    </Suspense>
  </React.StrictMode>,
);
